import { useMemo } from 'react'

import { useGamificationPages } from '@/shared/lib/api/gamification-pages'
import { useGamificationDataStore } from '@/shared/store/gamificationDataStore'
import type { GamificationPage } from '@/types/gamificationPage'

export interface PersistedGamificationPages {
  pages: GamificationPage[]
  /**
   * `false` enquanto nem o store nem a revalidação sabem quais são as páginas.
   *
   * "Conhecido e vazio" e "ainda não sei" são estados DIFERENTES: o primeiro é
   * configuração legítima do produtor, o segundo é ausência de dado. Colapsá-los
   * num array vazio é o que fazia a nav renderizar módulos padrão navegáveis.
   */
  known: boolean
}

/**
 * As páginas do produtor, servidas pelo bundle já persistido no dispositivo —
 * gravado e versionado pelo `GamificationDataInitializer`, e até então sem
 * leitor. Tira a navegação de série com a rede e com a resolução de sessão; o
 * REST passa a ser revalidação (design D7).
 */
export function usePersistedGamificationPages(): PersistedGamificationPages {
  const dataByProductId = useGamificationDataStore(
    state => state.dataByProductId
  )
  const activeDigitalProductId = useGamificationDataStore(
    state => state.activeDigitalProductId
  )

  const { records: pages, isSuccess } = useGamificationPages() as {
    records: GamificationPage[]
    isSuccess?: boolean
  }

  const persisted = useMemo(() => {
    if (!activeDigitalProductId) return undefined

    return dataByProductId[activeDigitalProductId]?.gamification_pages
  }, [dataByProductId, activeDigitalProductId])

  return useMemo(() => {
    // A revalidação vence quando chega: a barra já está montada com os itens
    // do store, então trocar a fonte não custa uma remontagem.
    if (pages && pages.length > 0) return { pages, known: true }
    if (persisted && persisted.length > 0) {
      return { pages: persisted, known: true }
    }

    // Resposta confirmada e vazia é conhecimento — o produtor não configurou
    // página alguma. Só a ausência de resposta é não-saber.
    return { pages: [], known: Boolean(isSuccess) }
  }, [pages, persisted, isSuccess])
}
